<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import {
  fetcherEvents,
  showServiceNotification,
} from "@/shared/lib/centrifuge";
import { generateRequestUuid } from "@/shared/lib/uuid";
import { toast } from "@/shared/store/alertStore";
import { useAuthedImage } from "@/shared/lib/composables/useAuthedImage";

import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import Button from "@/shared/ui/button/Button.vue";
import { FormSheet } from "@/shared/ui/form-sheet";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Skeleton } from "@/shared/ui/skeleton";
import { runChainService } from "@/entities/template";
import { getServiceErrorMessage } from "@/shared/lib/serviceError";
import {
  getPrimaryServiceCode,
  PARTICIPANT_KIND,
} from "@/entities/participant";

const props = defineProps({
  description: String,
  selectedRole: Object,
  execution: Object,
  participant: {
    type: Object,
    default: null,
  },
});

const isEditMode = computed(() => Boolean(props.participant?.uuid));

const emit = defineEmits(["save", "close"]);

const route = useRoute();
const { t } = useI18n();

const uuid = computed(() => route.params.id);

const visible = ref(false);
const isSaving = ref(false);
const isSearching = ref(false);
const searchCompleted = ref(false);
const foundPerson = ref(null);
const pendingRequestUuid = ref(null);
let unsubscribeWs = null;

const iinValue = ref("");
const { src: photoSrc, error: photoError } = useAuthedImage(
  () => foundPerson.value?.person_photo
);

const title = computed(() =>
  isEditMode.value
    ? t("Редактирование наследодателя")
    : t("Добавление наследодателя")
);

const descriptionText = computed(() => {
  return (
    props.description ||
    `${props.selectedRole?.name || "NASLEDODATEL"} — ${t("Физ лицо")}`
  );
});

const hasFoundPerson = computed(() => Boolean(foundPerson.value?.iin));

const hasDeathDate = computed(
  () => hasFoundPerson.value && !!foundPerson.value?.death_date
);

const isSaveDisabled = computed(() => {
  if (isSaving.value) return true;
  if (!hasFoundPerson.value) return true;
  if (!hasDeathDate.value) return true;
  return false;
});

const iinError = computed(() => {
  if (!iinValue.value) return null;
  if (iinValue.value.length !== 12) return t("ИИН должен содержать 12 цифр");
  return null;
});

async function searchGbdfl() {
  if (!props.execution) return;
  if (iinValue.value.length !== 12) return;

  const steps =
    props.execution?.steps_entity_types?.[PARTICIPANT_KIND.INDIVIDUAL];
  const gbdflStep = steps?.find(
    (s) => s.code === getPrimaryServiceCode(PARTICIPANT_KIND.INDIVIDUAL)
  );
  if (!gbdflStep) return;

  cleanupWs();
  isSearching.value = true;
  searchCompleted.value = false;
  foundPerson.value = null;

  try {
    const clientRequestUuid = generateRequestUuid();

    // Подписываемся ДО запуска — чтобы не пропустить быстрый WS-ответ (гонка).
    pendingRequestUuid.value = clientRequestUuid;
    unsubscribeWs = fetcherEvents.on(handleWsResponse);

    const res = await runChainService(
      props.execution.uuid,
      gbdflStep.uuid,
      uuid.value,
      {
        person_identifier: iinValue.value,
        request_uuid: clientRequestUuid,
      }
    );

    if (!res?.success || !res.payload?.run) {
      foundPerson.value = null;
      searchCompleted.value = true;
      toast.error(getServiceErrorMessage(res));
      cleanupWs();
      isSearching.value = false;
    }
  } catch (e) {
    console.error("searchGbdfl error:", e);
    toast.error(getServiceErrorMessage(e));
    cleanupWs();
    isSearching.value = false;
  }
}

function handleWsResponse(data) {
  if (data?.type !== "FETCHER") return;
  if (
    pendingRequestUuid.value &&
    data.request_uuid !== pendingRequestUuid.value
  ) {
    return;
  }

  const wsPayload = data?.payload;

  // PENDING — ещё обрабатывается, продолжаем ждать
  if (wsPayload?.status === "PENDING") return;

  // NOTIFICATION — ожидание подтверждения клиента (КДП), loading продолжается
  if (wsPayload?.status === "NOTIFICATION") {
    showServiceNotification(wsPayload);
    return;
  }

  // Cleanup подписки
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }

  if (
    wsPayload?.status === "FINISHED" &&
    wsPayload.data &&
    Object.keys(wsPayload.data).length > 0
  ) {
    foundPerson.value = wsPayload.data;
    toast.success(t("Данные получены"), {
      description: new Date().toLocaleTimeString(),
      duration: 3000,
    });
  } else {
    foundPerson.value = null;

    const errorMsg =
      wsPayload?.error_description ||
      t("Не удалось получить данные от сервиса");
    toast.error(errorMsg, {
      duration: 5000,
    });
  }

  searchCompleted.value = true;
  isSearching.value = false;
}

function cleanupWs() {
  pendingRequestUuid.value = null;
  if (unsubscribeWs) {
    unsubscribeWs();
    unsubscribeWs = null;
  }
}

onBeforeUnmount(() => {
  cleanupWs();
});

function save() {
  if (isSaveDisabled.value) return;

  const person = foundPerson.value;

  const participant = {
    participant_type_code: props.selectedRole?.code || "NASLEDODATEL",
    participant_type: "INDIVIDUAL",
    resident_kz: true,
    identifier: person.iin,
    first_name: person.first_name || "",
    last_name: person.last_name || "",
    middle_name: person.middle_name || "",
    person_photo: person.person_photo || "",
    birth_date: person.birth_date || "",
    death_date: person.death_date || "",
  };

  emit("save", participant);
}

function close() {
  visible.value = false;
  emit("close");
}

function populateFromParticipant() {
  const p = props.participant;
  if (!p) return;

  iinValue.value = p.identifier || "";
  foundPerson.value = {
    iin: p.identifier,
    first_name: p.first_name,
    last_name: p.last_name,
    middle_name: p.middle_name,
    person_photo: p.person_photo,
    birth_date: p.birth_date,
    death_date: p.death_date,
  };
  searchCompleted.value = true;
}

onMounted(() => {
  visible.value = true;
  if (isEditMode.value) {
    populateFromParticipant();
  }
});
</script>

<template>
  <FormSheet
    v-model:open="visible"
    :title="title"
    :description="descriptionText"
    :width="500"
    @close="close"
  >
    <div class="w-full h-full flex flex-col gap-6">
      <main class="flex flex-col gap-5 flex-1">
        <div class="flex flex-col gap-1 py-2">
          <FormItem float>
            <FormLabel>{{ $t("ИИН") }}</FormLabel>
            <FormControl>
              <Input v-model="iinValue" maxlength="12" />
            </FormControl>
            <span v-if="iinError" class="text-destructive text-xs mt-1">
              {{ iinError }}
            </span>
          </FormItem>

          <Button
            variant="outline"
            class="w-full"
            :disabled="iinValue.length !== 12 || isSearching"
            @click="searchGbdfl"
          >
            {{ $t("Запросить данные") }}
          </Button>
        </div>

        <div class="flex flex-col gap-4">
          <template v-if="isSearching">
            <p class="text-muted-foreground text-sm font-medium">
              {{ $t("Идет поиск по базам данных...") }}
            </p>
            <Skeleton class="h-28 w-full" />
          </template>

          <template v-else-if="searchCompleted">
            <p class="text-muted-foreground text-sm font-medium">
              {{ hasFoundPerson ? $t("Лицо найдено") : $t("Лицо не найдено") }}
            </p>

            <div
              v-if="hasFoundPerson"
              class="p-4 rounded-2xl border border-border flex gap-4 items-start"
            >
              <div v-if="photoSrc && !photoError">
                <img
                  :src="photoSrc"
                  :alt="foundPerson.last_name + ' ' + foundPerson.first_name"
                  class="w-16 h-[78px] object-cover rounded-xl border border-border"
                  @error="photoError = true"
                />
              </div>
              <div v-else class="empty">
                <svg-icon
                  name="huge-user"
                  class="w-6 h-16 text-muted-foreground"
                />
              </div>
              <div class="flex flex-col gap-2 flex-1">
                <div>
                  <p class="text-muted-foreground text-xs">
                    {{ $t("ФИО") }}
                  </p>
                  <p class="text-foreground text-sm">
                    {{ foundPerson.last_name }}
                    {{ foundPerson.first_name }}
                    {{ foundPerson.middle_name || "" }}
                  </p>
                </div>

                <div>
                  <p class="text-muted-foreground text-xs">
                    {{ $t("ИИН") }}
                  </p>
                  <p class="text-foreground text-sm">
                    {{ foundPerson.iin }}
                  </p>
                </div>

                <div
                  v-if="foundPerson.birth_date || foundPerson.death_date"
                  class="w-full flex gap-4"
                >
                  <div v-if="foundPerson.birth_date" class="w-1/2">
                    <p class="text-muted-foreground text-xs">
                      {{ $t("Дата рождения") }}
                    </p>
                    <p class="text-foreground text-sm">
                      {{ foundPerson.birth_date }}
                    </p>
                  </div>
                  <div v-if="foundPerson.death_date" class="w-1/2">
                    <p class="text-muted-foreground text-xs">
                      {{ $t("Дата смерти") }}
                    </p>
                    <p class="text-foreground text-sm">
                      {{ foundPerson.death_date }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="hasFoundPerson && !hasDeathDate"
              class="w-full px-4 py-2.5 rounded-md flex gap-2.5 items-center"
              style="background: rgba(255, 195, 196, 0.24)"
            >
              <svg-icon
                name="huge-x"
                class="w-4 h-4 shrink-0 text-foreground"
              />
              <p class="text-foreground text-sm leading-6">
                {{ $t("Актовая запись о смерти не найдена") }}
              </p>
            </div>
          </template>
        </div>
      </main>
    </div>

    <template #footer>
      <div class="flex-1"></div>
      <Button variant="outline" @click="close">
        {{ $t("Отмена") }}
      </Button>
      <Button
        class="ml-2"
        variant="default"
        :disabled="isSaveDisabled"
        @click="save"
      >
        {{
          isSaving
            ? isEditMode
              ? $t("Сохранение...")
              : $t("Добавление...")
            : isEditMode
            ? $t("Сохранить")
            : $t("Добавить")
        }}
      </Button>
    </template>
  </FormSheet>
</template>

<style lang="scss" scoped>
.empty {
  @apply w-[72px] h-[88px] flex justify-center items-center bg-muted/50;
  @apply rounded-xl border border-border;
}
</style>
